import { create } from 'zustand';
import type { PlatformPrincipal } from './types';

// Mirrors features/auth/authV2Store.ts in shape, but is a WHOLLY SEPARATE
// auth surface: distinct localStorage keys, no roles/permissions (the LLD's
// platform principal carries no perms — being a platform principal *is* the
// authorization), and never shares state with the org app's authV2Store.

export const PLATFORM_STORAGE_KEYS = {
  ACCESS_TOKEN: 'hw_pf_access',
  REFRESH_TOKEN: 'hw_pf_refresh',
  DEVICE_ID: 'hw_pf_device_id',
} as const;

function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(PLATFORM_STORAGE_KEYS.DEVICE_ID);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(PLATFORM_STORAGE_KEYS.DEVICE_ID, id);
  return id;
}

export type PlatformAuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

export interface PlatformSessionTokens {
  accessToken: string;
  refreshToken: string;
}

interface PlatformAuthState {
  accessToken: string | null;
  refreshToken: string | null;
  principal: PlatformPrincipal | null;
  status: PlatformAuthStatus;
  deviceId: string;

  setSession: (tokens: PlatformSessionTokens, principal: PlatformPrincipal) => void;
  updateTokens: (tokens: PlatformSessionTokens) => void;
  setPrincipal: (principal: PlatformPrincipal) => void;
  logout: () => void;
  hydrateFromStorage: () => void;
}

export const usePlatformAuthStore = create<PlatformAuthState>((set) => ({
  accessToken: null,
  refreshToken: null,
  principal: null,
  status: 'idle',
  deviceId: getOrCreateDeviceId(),

  setSession(tokens, principal) {
    localStorage.setItem(PLATFORM_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(PLATFORM_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      principal,
      status: 'authenticated',
    });
  },

  updateTokens(tokens) {
    localStorage.setItem(PLATFORM_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(PLATFORM_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
    set({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
  },

  setPrincipal(principal) {
    set({ principal });
  },

  logout() {
    localStorage.removeItem(PLATFORM_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(PLATFORM_STORAGE_KEYS.REFRESH_TOKEN);
    set({ accessToken: null, refreshToken: null, principal: null, status: 'unauthenticated' });
  },

  hydrateFromStorage() {
    const access = localStorage.getItem(PLATFORM_STORAGE_KEYS.ACCESS_TOKEN);
    const refresh = localStorage.getItem(PLATFORM_STORAGE_KEYS.REFRESH_TOKEN);
    if (access && refresh) {
      set({ accessToken: access, refreshToken: refresh, status: 'authenticated' });
    } else {
      set({ status: 'unauthenticated' });
    }
  },
}));

export function useIsPlatformAuthenticated(): boolean {
  return usePlatformAuthStore((s) => s.status === 'authenticated' && s.accessToken !== null);
}
